import { useState, type ChangeEvent } from 'react';
import { line, Color, Size, fill, formatDate, photoPositionLine, t, type DateFormat } from '@bms2/shared';
import { Button, TextInput, Textarea } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { patch, post, upload, useApi, FILES_PATH } from './api';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { fileScanTitle } from './tileItems';
import { FileStorageBlock } from './FileStorageBlock';
import styles from './RecordPage.module.css';

export interface FileDetailsRow {
  id: string;
  name: string;
  /** The description the file carries, in the uploader's own words; the box opens on it (decision 0605). */
  description: string;
  /** How many versions the file is on, which the type line states rather than the description (decision 0605). */
  version: number | null;
  typeName: string | null;
}

/** One version as the file's detail route answers it (decision 2421). */
export interface FileVersionLine {
  id: string;
  versionNumber: number;
  uploadedAt: string;
  uploadedByName: string | null;
  originalFilename: string | null;
  storedName: string | null;
  /** 🔒 Decision 3012: the version's scan, which the list names with its date, time and scanner; absent from an older reply. */
  scanVerdict?: 'Pending' | 'Clean' | 'Blocked' | null;
  scannedAt?: string | null;
  scannedBy?: string | null;
}

/** 🔒 Decision 3012: what a version's scan says, Scanned with its date, time and scanner, or Scan pending; nothing for any other. */
function versionScan(version: FileVersionLine, zone: string, format: DateFormat): string | null {
  return fileScanTitle(version.scanVerdict ?? null, version.scannedAt ?? null, version.scannedBy ?? null, zone, format);
}

/**
 * 🔒 The version list (decision 2421): each version, newest first, with the name
 * its own file carried, who uploaded it and when, and the name it is stored
 * under. The document's own name stays its first upload's, so this list is
 * where a later file's own name is read.
 */
export function FileVersionList({ versions }: { versions: readonly FileVersionLine[] }) {
  const zone = useViewerZone();
  const format = useDateFormat();
  const newestFirst = [...versions].sort((a, b) => b.versionNumber - a.versionNumber);
  return (
    <div className={styles.field} data-file-versions={newestFirst.length}>
      <span className={styles.fieldLabel}>{t('file.details.versions')}</span>
      <ol className={styles.versionList}>
        {newestFirst.map((version) => (
          <li key={version.id} data-file-version={version.versionNumber}>
            <span>{fill(t('file.details.versionLine'), { number: String(version.versionNumber), name: version.originalFilename ?? '', person: version.uploadedByName ?? t('file.details.unknownUploader'), date: formatDate(version.uploadedAt, zone, format) })}</span>
            {version.storedName ? <span className={styles.hint} data-file-stored-name={version.storedName}>{fill(t('file.details.storedAs'), { stored: version.storedName })}</span> : null}
            {/* 🔒 Decision 3012: when the version was scanned and by which scanner, which a clean row's shield carries in its title. */}
            {versionScan(version, zone, format) ? <span className={styles.hint} data-file-version-scan={version.scanVerdict ?? ''}>{versionScan(version, zone, format)}</span> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * File details from a file row (piece 13; decision 0273): the type it was
 * uploaded as, its description, a note kept with the file, a new version and
 * Delete. 🔨 Delete is the trash, restorable by an administrator; nothing here
 * removes bytes.
 */
export function FileDetailsModal({ open, file, apiBaseUrl = '', onCancel, onSaved }: { open: boolean; file: FileDetailsRow | null; apiBaseUrl?: string; onCancel: () => void; onSaved: () => void }) {
  // 🔨 The box opens on the description the file carries, and the modal is keyed to
  // the file, so reopening it on another row reads that row's words (decision 0605).
  const [description, setDescription] = useState(file?.description ?? '');
  const [note, setNote] = useState('');
  const [version, setVersion] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const detail = useApi<{ versions: FileVersionLine[]; file?: { photoKind?: string | null; latitude?: number | null; longitude?: number | null } }>(open && file ? `${FILES_PATH}/${file.id}` : null, apiBaseUrl);
  // 🔒 Decision 5440: an existing-equipment photo's kind, and the position it was taken at where the phone recorded one.
  const facts = detail.state.kind === 'ready' ? detail.state.data.file : undefined;
  const photoKind = facts?.photoKind ?? null;
  const position = facts ? photoPositionLine(facts) : null;
  if (!file) return null;
  const pick = (event: ChangeEvent<HTMLInputElement>) => setVersion(event.target.files?.[0] ?? null);
  const save = async () => {
    setError(null);
    // 🔨 The description goes whenever it changed, a cleared one included: the route
    // takes null, and a box emptied on purpose has to reach the file (decision 0605).
    const wanted = description.trim();
    if (wanted !== (file.description ?? '')) {
      const described = await patch(`${FILES_PATH}/${file.id}`, { description: wanted === '' ? null : wanted }, apiBaseUrl);
      if (!described.ok) return setError(described.message);
    }
    if (note.trim() !== '') {
      const noted = await post(`/pages/notes/File/${file.id}`, { text: note.trim() }, apiBaseUrl);
      if (!noted.ok) return setError(noted.message);
    }
    if (version) {
      const form = new FormData();
      form.append('file', version, version.name);
      const versioned = await upload(`${FILES_PATH}/${file.id}/versions`, form, apiBaseUrl);
      if (!versioned.ok) return setError(versioned.message);
    }
    setMessage(t('file.details.saved'));
    onSaved();
    onCancel();
  };
  const remove = async () => {
    const trashed = await post(`${FILES_PATH}/${file.id}/trash`, {}, apiBaseUrl);
    if (!trashed.ok) return setError(trashed.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('file.details.title')} subtitle={file.name} onCancel={onCancel} dataModal="file-details" actions={[{ kind: 'primary', label: t('modal.save'), onAct: () => void save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {message ? <AlertBanner tone="good" message={message} /> : null}
      {/* 🔨 The type line names the type and the version and never repeats the
          description, which the box below holds (decision 0605). */}
      <p className={styles.hint} data-file-type={file.typeName ?? ''}>{line(t('file.details.type'), '·', file.typeName ?? '', file.version ? `· v${file.version}` : null)}</p>
      {photoKind ? (
        <p className={styles.hint} data-file-photo-kind={photoKind} data-file-photo-position={position ?? ''}>
          {line(t('equipment.kind'), '·', photoKind, '·', position ? line(t('equipment.position'), '·', position) : t('equipment.positionNone'))}
        </p>
      ) : null}
      {detail.state.kind === 'ready' && (detail.state.data.versions ?? []).length > 0 ? <FileVersionList versions={detail.state.data.versions} /> : null}
      {/* 🔒 Decision 7620: the stored object behind the file, for a super admin alone. */}
      {open ? <FileStorageBlock fileId={file.id} apiBaseUrl={apiBaseUrl} /> : null}
      <TextInput size={Size.Large} label={t('file.details.description')} value={description} onChange={setDescription} name="description" />
      <Textarea size={Size.Large} label={t('file.details.note')} value={note} onChange={setNote} name="note" />
      <p className={styles.hint}>{t('file.details.noteHint')}</p>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>{t('file.details.newVersion')}</span>
        <input type="file" onChange={pick} data-file-version-input="true" />
      </label>
      <div className={styles.actions}>
        <Button
          size={Size.Small}
          color={Color.Destructive}
          text={[t('file.details.delete')]}
          actions={[() => void remove()]}
          confirm={{ title: t('file.details.deleteTitle'), consequence: { body: t('file.details.deleteBody') }, confirmLabel: t('file.details.deleteConfirm') }}
        />
      </div>
    </Modal>
  );
}
